import React, { useRef, useEffect } from 'react';
import CtaSection from '@/components/CtaSection';
import { Button } from '@/components/ui/button';
import AnimatedTestimonials from '@/components/AnimatedTestimonials';
import { 
  Brain, 
  Search, 
  Code, 
  Rocket, 
  ArrowRight, 
  ArrowDown,
  BrainCircuit,
  Briefcase,
  Building,
  ShoppingCart,
  Banknote,
  Heart,
  Building2,
  GraduationCap,
  Scale,
  Megaphone,
  Truck,
  Hotel,
  Church,
  Factory,
  Dumbbell
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useIsMobile } from '@/hooks/use-mobile';

interface ProcessStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const processSteps: ProcessStep[] = [
  {
    id: 1,
    title: "Discovery",
    description: "We understand your vision, goals & branding needs. Through detailed consultations, we identify your target audience, business objectives, and unique value proposition to create a tailored app strategy.",
    icon: <Search className="h-8 w-8" />,
    color: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  },
  {
    id: 2,
    title: "AI-Powered Design",
    description: "Automated prototyping for speed & efficiency. Our AI systems generate multiple design options based on your requirements, allowing for rapid iteration and refinement while maintaining design consistency.",
    icon: <BrainCircuit className="h-8 w-8" />,
    color: "bg-purple-500/10 text-purple-500 border-purple-500/20",
  },
  {
    id: 3,
    title: "Development & Optimization",
    description: "Seamless coding with built-in security scanning & responsiveness checks. Our developers translate designs into clean, efficient code while our AI systems continuously test for performance, security vulnerabilities, and cross-platform compatibility.",
    icon: <Code className="h-8 w-8" />,
    color: "bg-green-500/10 text-green-500 border-green-500/20",
  },
  {
    id: 4,
    title: "Launch & Support",
    description: "Deploy, monitor, and refine for continued perfection. We ensure a smooth launch across all platforms and provide ongoing monitoring, maintenance, and updates to keep your app performing at its best.",
    icon: <Rocket className="h-8 w-8" />,
    color: "bg-red-500/10 text-red-500 border-red-500/20",
  },
];

interface Industry {
  id: string;
  title: string;
  description: string;
  examples: string;
  icon: React.ReactNode;
  category: string;
  name: string;
  backgroundImageSrc?: string;
  imageAlt?: string;
}

const industries: Industry[] = [
  {
    id: "1️⃣",
    title: "Small & Medium-Sized Businesses (SMBs)",
    description: "Struggling with outdated systems? We build custom business automation tools to save time and resources.",
    examples: "CRM, workflow automation, invoicing tools, customer portals.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Briefcase className="h-16 w-16 text-white" /></div>,
    category: "business",
    name: "Small & Medium-Sized Businesses (SMBs)",
    backgroundImageSrc: "/lovable-uploads/026a0287-2424-4b88-9239-038c2ea93f1e.png",
    imageAlt: "Office workspace with computers representing small and medium businesses"
  },
  {
    id: "2️⃣",
    title: "Startups & Entrepreneurs",
    description: "Need MVPs (Minimum Viable Products) fast? We build scalable, AI-enhanced web apps at affordable prices.",
    examples: "Landing pages, booking systems, AI chatbots, automated lead management.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Rocket className="h-16 w-16 text-white" /></div>,
    category: "business",
    name: "Startups & Entrepreneurs",
    backgroundImageSrc: "/lovable-uploads/071231bc-7ebb-4c11-b2d8-241e00e8cb83.png",
    imageAlt: "Modern workspace representing startup environment"
  },
  {
    id: "3️⃣",
    title: "E-commerce & Retail",
    description: "Want to automate orders, customer support, and inventory? We design powerful AI-driven tools to increase efficiency and sales.",
    examples: "Smart chatbots, inventory tracking, dynamic pricing systems, recommendation engines.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><ShoppingCart className="h-16 w-16 text-white" /></div>,
    category: "business",
    name: "E-commerce & Retail",
    backgroundImageSrc: "/lovable-uploads/a35d298f-227e-489d-9ae0-c880eccffe33.png",
    imageAlt: "E-commerce retail environment with shopping related items"
  },
  {
    id: "4️⃣",
    title: "Finance & FinTech",
    description: "Optimize workflows, compliance tracking, and customer service with AI-driven systems.",
    examples: "Budget trackers, AI-powered fraud detection, invoice automation, payment processing dashboards.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Banknote className="h-16 w-16 text-white" /></div>,
    category: "business",
    name: "Finance & FinTech",
    backgroundImageSrc: "/lovable-uploads/239fb574-6f84-493a-b803-112909b8a0a7.png",
    imageAlt: "Financial charts and graphs representing finance industry"
  },
  {
    id: "5️⃣",
    title: "Healthcare & Wellness",
    description: "Reduce manual work and improve patient experience with intelligent scheduling and medical record automation.",
    examples: "Telemedicine platforms, appointment booking, AI-powered symptom checkers.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Heart className="h-16 w-16 text-white" /></div>,
    category: "health",
    name: "Healthcare & Wellness",
    backgroundImageSrc: "/lovable-uploads/dab58355-0538-4742-8357-6dcff5ec8c58.png",
    imageAlt: "Healthcare setting with medical equipment"
  },
  {
    id: "6️⃣",
    title: "Real Estate & Property Management",
    description: "Automate property listings, lead qualification, and rental management with AI-driven platforms.",
    examples: "Property listing automation, AI chatbots for lead generation, smart lease management systems.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Building2 className="h-16 w-16 text-white" /></div>,
    category: "property",
    name: "Real Estate & Property Management",
    backgroundImageSrc: "/lovable-uploads/1730a54d-0822-42b8-abc3-0e3b600a22b2.png",
    imageAlt: "Modern architecture representing real estate industry"
  },
  {
    id: "7️⃣",
    title: "Education & Online Coaching",
    description: "Build powerful LMS (Learning Management Systems) or automate student management and support.",
    examples: "Online course platforms, student tracking, AI-powered tutoring assistants.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><GraduationCap className="h-16 w-16 text-white" /></div>,
    category: "education",
    name: "Education & Online Coaching",
    backgroundImageSrc: "/lovable-uploads/e29748cd-7584-4f2a-a3fc-7ef6aedae814.png",
    imageAlt: "Educational environment with books and learning materials"
  },
  {
    id: "8️⃣",
    title: "Legal & Consulting Services",
    description: "AI can streamline contracts, manage client interactions, and automate repetitive tasks.",
    examples: "Document automation, legal chatbots, AI-powered client intake forms.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Scale className="h-16 w-16 text-white" /></div>,
    category: "services",
    name: "Legal & Consulting Services",
    backgroundImageSrc: "/lovable-uploads/a4c74cb0-cbdf-453b-b618-ec31dd677efc.png",
    imageAlt: "Legal office with books and documents"
  },
  {
    id: "9️⃣",
    title: "Marketing & Creative Agencies",
    description: "Eliminate bottlenecks in client management, campaign tracking, and content automation.",
    examples: "AI content generators, automated reporting dashboards, smart client CRMs.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Megaphone className="h-16 w-16 text-white" /></div>,
    category: "services",
    name: "Marketing & Creative Agencies",
    backgroundImageSrc: "/lovable-uploads/5c56ffbc-0aba-4962-b5ca-91404cb7761c.png",
    imageAlt: "Creative agency workspace with design elements"
  },
  {
    id: "🔟",
    title: "Logistics & Supply Chain",
    description: "Optimize inventory, fleet tracking, and shipment automation with AI-driven logistics solutions.",
    examples: "Route optimization, automated dispatch, inventory forecasting tools.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Truck className="h-16 w-16 text-white" /></div>,
    category: "logistics",
    name: "Logistics & Supply Chain",
    backgroundImageSrc: "/lovable-uploads/d3eb5bc5-4d9e-42de-b132-5812d1b06708.png",
    imageAlt: "Logistics warehouse with shipping containers and packages"
  },
  {
    id: "1️⃣1️⃣",
    title: "Hospitality & Travel",
    description: "AI-powered tools to streamline bookings, reservations, and customer experience.",
    examples: "AI booking assistants, smart itinerary planners, automated customer support chatbots.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Hotel className="h-16 w-16 text-white" /></div>,
    category: "hospitality",
    name: "Hospitality & Travel",
    backgroundImageSrc: "/lovable-uploads/a35d298f-227e-489d-9ae0-c880eccffe33.png", // Reusing image as placeholder
    imageAlt: "Hotel lobby representing hospitality industry"
  },
  {
    id: "1️⃣2️⃣",
    title: "Churches & Nonprofits",
    description: "Automate donation tracking, member management, and event coordination with custom web apps.",
    examples: "Church management systems, donor tracking, event registration portals.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Church className="h-16 w-16 text-white" /></div>,
    category: "nonprofit",
    name: "Churches & Nonprofits",
    backgroundImageSrc: "/lovable-uploads/a4c74cb0-cbdf-453b-b618-ec31dd677efc.png", // Reusing image as placeholder
    imageAlt: "Community gathering representing nonprofit organizations"
  },
  {
    id: "1️⃣3️⃣",
    title: "Manufacturing & Production",
    description: "AI-powered automation of inventory, production scheduling, and order management.",
    examples: "Smart factory dashboards, production workflow automation, quality control tracking.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Factory className="h-16 w-16 text-white" /></div>,
    category: "industrial",
    name: "Manufacturing & Production",
    backgroundImageSrc: "/lovable-uploads/d3eb5bc5-4d9e-42de-b132-5812d1b06708.png", // Reusing image as placeholder
    imageAlt: "Factory floor with manufacturing equipment"
  },
  {
    id: "1️⃣4️⃣",
    title: "Beauty, Wellness, and Fitness",
    description: "Scale coaching and client management with AI-driven scheduling and virtual assistance tools.",
    examples: "AI fitness assistants, automated appointment booking, subscription-based coaching platforms.",
    icon: <div className="flex items-center justify-center bg-gold p-2 rounded-full shadow-md border border-gold-dark/20"><Dumbbell className="h-16 w-16 text-white" /></div>,
    category: "health",
    name: "Beauty, Wellness, and Fitness",
    backgroundImageSrc: "/lovable-uploads/dab58355-0538-4742-8357-6dcff5ec8c58.png", // Reusing image as placeholder
    imageAlt: "Fitness studio representing wellness and fitness industry"
  },
];

const categories = [
  { id: "all", label: "All Industries" },
  { id: "business", label: "Business & Commerce" },
  { id: "health", label: "Health & Wellness" },
  { id: "services", label: "Professional Services" },
  { id: "education", label: "Education" },
  { id: "property", label: "Real Estate" },
  { id: "logistics", label: "Logistics" },
  { id: "hospitality", label: "Hospitality" },
  { id: "nonprofit", label: "Nonprofit" },
  { id: "industrial", label: "Industrial" },
];

const Process = () => {
  const stepsRef = useRef<HTMLDivElement>(null);
  const infographicRef = useRef<HTMLDivElement>(null);
  const industriesRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    [stepsRef, infographicRef, industriesRef].forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      [stepsRef, infographicRef, industriesRef].forEach((ref) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-grow">
        <PageHeader
          icon={<Brain size={16} />}
          badgeText="Our Process"
          title={<>The AI-Powered <span className="text-gold">Magic</span> Behind Our Luxury Apps</>}
          subtitle="Discover our streamlined approach that combines human creativity with artificial intelligence
          to deliver exceptional results in a fraction of the time and cost."
        />
        
        <section 
          className="py-16 px-6 md:px-10 bg-luxury-50"
          ref={infographicRef}
          style={{ opacity: 0 }}
        >
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-luxury p-8 md:p-12 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-red-500"></div>
              
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">
                How Our AI-Powered Development <span className="text-highlight">Workflow</span> Works
              </h2>
              
              <div className="relative">
                <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-luxury-100 -translate-x-1/2 z-0"></div>
                
                {processSteps.map((step, index) => (
                  <div 
                    key={step.id} 
                    className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 mb-16 last:mb-0 relative z-10`}
                  >
                    <div className="flex-1">
                      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${step.color} mb-4 md:mb-0`}>
                        {step.icon}
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-luxury-900 mb-3">{step.title}</h3>
                      <p className="text-luxury-600">{step.description}</p>
                    </div>
                    
                    <div className="hidden md:flex items-center justify-center w-16">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full bg-white border-2 border-luxury-100 shadow-md`}>
                        <span className="font-bold text-luxury-900">{step.id}</span>
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      {/* Empty div to maintain layout */}
                    </div>
                    
                    {index < processSteps.length - 1 && (
                      <div className="hidden md:block absolute -bottom-8 left-1/2 -translate-x-1/2 text-luxury-300">
                        <ArrowDown className="h-6 w-6" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        
        <section 
          className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-10 bg-white"
          ref={industriesRef}
          style={{ opacity: 0 }}
        >
          <div className="container mx-auto">
            <AnimatedTestimonials 
              items={industries} 
              autoplay={true}
              isIndustryDisplay={true}
              title={
                <div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3 sm:mb-4">
                    Industries & Businesses That Need Our <span className="text-highlight">Solutions</span>
                  </h2>
                  <p className="text-luxury-600 text-center max-w-3xl mx-auto mb-6 sm:mb-8 text-sm sm:text-base">
                    We serve a wide range of industries with our AI-powered web applications and custom internal tools.
                    <span className="hidden md:inline"> Explore our solutions below:</span>
                  </p>
                </div>
              }
            />
          </div>
        </section>
        
        <CtaSection />
      </main>
    </div>
  );
};

export default Process;
